import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, FileText, CheckCircle2, Clock, RotateCcw, Copy, ChevronRight, ChevronDown, AlertTriangle } from 'lucide-react';
import client from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import DateRangeFilter from '../../components/shared/DateRangeFilter';
import { NAVY, AMBER, AMBER_DARK, BORDER_COLOR, bubbleStyle, navyBtn, fieldStyle } from '../../styles/tokens';
import { STATUS_STYLE } from '@shared/constants';

// Colors from the shared STATUS_STYLE (single source); labels + icons stay local (unchanged).
const STATUS_CONFIG = {
  draft:            { label: 'Borrador',            ...STATUS_STYLE.draft,            icon: FileText },
  returned:         { label: 'Devuelto',            ...STATUS_STYLE.returned,         icon: RotateCcw },
  submitted:        { label: 'Pend. Ingeniero',     ...STATUS_STYLE.submitted,        icon: Clock },
  pending_engineer: { label: 'Pend. Ingeniero',     ...STATUS_STYLE.pending_engineer, icon: Clock },
  pending_manager:  { label: 'Pend. Gerente',       ...STATUS_STYLE.pending_manager,  icon: Clock },
  pending_clerk:    { label: 'Rev. Final',          ...STATUS_STYLE.pending_clerk,    icon: Clock },
  approved:         { label: 'Aprobado',            ...STATUS_STYLE.approved,         icon: CheckCircle2 },
};

// Status groups in display order
const STATUS_GROUPS = [
  { key: 'draft',            heading: 'Borradores' },
  { key: 'returned',         heading: 'Devueltos' },
  { key: 'submitted',        heading: 'Sometidos — Pendiente Aprobación' },
  { key: 'pending_engineer', heading: 'En Revisión — Ingeniero' },
  { key: 'pending_manager',  heading: 'En Revisión — Gerente' },
  { key: 'pending_clerk',    heading: 'Revisión Final' },
  { key: 'approved',         heading: 'Aprobados' },
];

const EDITABLE_STATUSES = new Set(['draft', 'returned']);

function fmtDate(d) {
  if (!d) return '';
  const [y, m, day] = String(d).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
}

function addDays(dateStr, days) {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function ProjectSheetsPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [project, setProject] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);

  // Date range filter
  const [filterFrom, setFilterFrom] = useState(null);
  const [filterTo, setFilterTo] = useState(null);

  // Collapsible sections
  const [collapsed, setCollapsed] = useState({});

  // Alerts
  const [alertMsg, setAlertMsg] = useState(null);
  const [gapAlert, setGapAlert] = useState(null);
  const [holidayAlert, setHolidayAlert] = useState(null);

  // New tarjeta date (editable)
  const [newDate, setNewDate] = useState('');

  // Holidays
  const [holidays, setHolidays] = useState([]);

  // Clone
  const [cloneSourceId, setCloneSourceId] = useState(null);
  const [cloneDate, setCloneDate] = useState(() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  });

  useEffect(() => {
    const year = new Date().getFullYear();
    Promise.all([
      client.get('/api/projects'),
      client.get('/api/nomina/sheets', { params: { view: 'project', project_id: projectId } }),
      client.get(`/api/admin/holidays?year=${year}`).catch(() => ({ data: [] })),
    ]).then(([projRes, sheetsRes, holRes]) => {
      const p = projRes.data.find(proj => proj.id === parseInt(projectId));
      setProject(p);
      setSheets(sheetsRes.data || []);
      setHolidays((holRes.data || []).filter(h => h.is_active !== false));
    }).catch(console.error).finally(() => setLoading(false));
  }, [projectId]);

  // Compute smart default date for new tarjeta (capped to today)
  function getNextDate() {
    const today = new Date().toISOString().slice(0, 10);
    if (sheets.length === 0) return today;
    const sorted = [...sheets].sort((a, b) =>
      new Date(b.sheet_date) - new Date(a.sheet_date)
    );
    const next = addDays(String(sorted[0].sheet_date).slice(0, 10), 1);
    return next > today ? today : next;
  }

  // Set default date once sheets load
  useEffect(() => {
    if (!loading && !newDate) setNewDate(getNextDate());
  }, [loading]);

  // Check for gaps and holidays when date changes
  function checkDateAlerts(dateStr) {
    setGapAlert(null);
    setHolidayAlert(null);
    if (!dateStr) return;

    // Gap detection: find missing weekdays (excluding holidays) between last sheet and selected date
    if (sheets.length > 0) {
      const sheetDates = new Set(sheets.map(s => String(s.sheet_date).slice(0, 10)));
      const holidayDates = new Set(holidays.map(h => String(h.date).slice(0, 10)));
      const sorted = [...sheets].sort((a, b) => new Date(b.sheet_date) - new Date(a.sheet_date));
      const lastDate = String(sorted[0].sheet_date).slice(0, 10);
      const missing = [];
      let d = addDays(lastDate, 1);
      while (d < dateStr) {
        const dayOfWeek = new Date(d + 'T12:00:00').getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6 && !sheetDates.has(d) && !holidayDates.has(d)) { // skip weekends & holidays
          missing.push(d);
        }
        d = addDays(d, 1);
      }
      if (missing.length > 0) {
        setGapAlert(`Faltan tarjetas para: ${missing.map(m => fmtDate(m)).join(', ')}`);
      }
    }

    // Holiday check
    const hol = holidays.find(h => String(h.date).slice(0, 10) === dateStr);
    if (hol) {
      setHolidayAlert(`${fmtDate(dateStr)} es feriado: ${hol.name}. ¿Desea continuar?`);
    }
  }

  // Filter sheets by date range
  const filteredSheets = sheets.filter(s => {
    const d = String(s.sheet_date).slice(0, 10);
    if (filterFrom && d < filterFrom) return false;
    if (filterTo && d > filterTo) return false;
    return true;
  });

  // Group sheets by status, sorted by date (older first)
  const sorted = [...filteredSheets].sort((a, b) => new Date(a.sheet_date) - new Date(b.sheet_date));
  const sheetsByStatus = {};
  for (const s of sorted) {
    if (!sheetsByStatus[s.status]) sheetsByStatus[s.status] = [];
    sheetsByStatus[s.status].push(s);
  }

  async function handleNewTarjeta() {
    setAlertMsg(null);
    const targetDate = newDate || getNextDate();

    try {
      const res = await client.post('/api/nomina/sheets', {
        project_id: parseInt(projectId),
        sheet_date: targetDate,
      });
      // Created or returned existing draft — navigate to it
      navigate(`/horas/tarjeta/${res.data.id}`);
    } catch (err) {
      if (err.response?.status === 409) {
        setAlertMsg(err.response.data.error);
      } else {
        alert(err.response?.data?.error || 'Error al crear tarjeta.');
      }
    }
  }

  async function handleClone() {
    if (!cloneSourceId || !cloneDate) return;
    try {
      const res = await client.post(`/api/nomina/sheets/${cloneSourceId}/clone`, {
        sheet_date: cloneDate,
      });
      setCloneSourceId(null);
      navigate(`/horas/tarjeta/${res.data.id}`);
    } catch (err) {
      alert(err.response?.data?.error || 'Error al copiar tarjeta.');
    }
  }

  function toggleCollapse(key) {
    setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Cargando...</div>;

  return (
    <div className="page-pad" style={{ padding: '20px 24px', maxWidth: 800, margin: '0 auto' }}>
      {/* Back */}
      <button onClick={() => navigate('/horas')} style={{
        display: 'flex', alignItems: 'center', gap: 6,
        background: 'none', border: 'none', cursor: 'pointer',
        color: '#64748b', fontSize: 13, marginBottom: 14, fontFamily: 'Inter, sans-serif',
      }}>
        <ArrowLeft size={16} /> Volver
      </button>

      {/* Project header */}
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 18, fontWeight: 700, color: NAVY, margin: '0 0 4px' }}>
          {project?.code ? `${project.code} ` : ''}{project?.name || 'Proyecto'}
        </h1>
        {project?.client && (
          <div style={{ fontSize: 12, color: '#94a3b8' }}>{project.client}</div>
        )}
      </div>

      {/* Filters + New button */}
      <div style={{ ...bubbleStyle, padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'space-between' }}>
        <DateRangeFilter
          dateFrom={filterFrom}
          dateTo={filterTo}
          onChange={({ dateFrom, dateTo }) => { setFilterFrom(dateFrom); setFilterTo(dateTo); }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            type="date"
            value={newDate}
            max={new Date().toISOString().slice(0, 10)}
            onChange={e => { setNewDate(e.target.value); checkDateAlerts(e.target.value); }}
            style={{ ...fieldStyle, width: 'auto', padding: '5px 8px', fontSize: 12 }}
          />
          <button onClick={handleNewTarjeta} style={{
            ...navyBtn, display: 'flex', alignItems: 'center', gap: 6,
            padding: '7px 14px', fontSize: 12,
          }}>
            <Plus size={14} /> Nueva Tarjeta
          </button>
        </div>
      </div>

      {/* Gap alert — missing days */}
      {gapAlert && (
        <div style={{
          ...bubbleStyle, padding: '10px 16px', marginBottom: 10,
          borderLeft: '3px solid #f59e0b', background: '#fffbeb',
          display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#92400e',
        }}>
          <AlertTriangle size={14} style={{ flexShrink: 0 }} />
          {gapAlert}
          <button onClick={() => setGapAlert(null)} style={{
            marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer',
            color: '#92400e', fontWeight: 600, fontSize: 16, lineHeight: 1,
          }}>×</button>
        </div>
      )}

      {/* Holiday alert */}
      {holidayAlert && (
        <div style={{
          ...bubbleStyle, padding: '10px 16px', marginBottom: 10,
          borderLeft: '3px solid #7c3aed', background: '#f5f3ff',
          display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#5b21b6',
        }}>
          <AlertTriangle size={14} style={{ flexShrink: 0 }} />
          {holidayAlert}
          <button onClick={() => setHolidayAlert(null)} style={{
            marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer',
            color: '#5b21b6', fontWeight: 600, fontSize: 16, lineHeight: 1,
          }}>×</button>
        </div>
      )}

      {/* Duplicate date alert */}
      {alertMsg && (
        <div style={{
          ...bubbleStyle, padding: '10px 16px', marginBottom: 14,
          borderLeft: '3px solid #f59e0b', background: '#fffbeb',
          display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#92400e',
        }}>
          <AlertTriangle size={16} style={{ flexShrink: 0 }} />
          {alertMsg}
          <button onClick={() => setAlertMsg(null)} style={{
            marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer',
            color: '#92400e', fontWeight: 600, fontSize: 16, lineHeight: 1,
          }}>×</button>
        </div>
      )}

      {/* Status buckets */}
      {filteredSheets.length === 0 ? (
        <div style={{ ...bubbleStyle, padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
          {sheets.length === 0
            ? 'No hay tarjetas para este proyecto. Crea una nueva tarjeta.'
            : 'No hay tarjetas en el rango de fechas seleccionado.'}
        </div>
      ) : (
        STATUS_GROUPS.filter(g => sheetsByStatus[g.key]?.length > 0).map(group => {
          const groupSheets = sheetsByStatus[group.key];
          const cfg = STATUS_CONFIG[group.key];
          const isCollapsed = collapsed[group.key];
          return (
            <div key={group.key} style={{ marginBottom: 20 }}>
              {/* Group header — clickable to collapse */}
              <div
                onClick={() => toggleCollapse(group.key)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  marginBottom: isCollapsed ? 0 : 8, cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                {isCollapsed
                  ? <ChevronRight size={16} style={{ color: '#64748b' }} />
                  : <ChevronDown size={16} style={{ color: '#64748b' }} />
                }
                <div style={{
                  width: 10, height: 10, borderRadius: '50%',
                  background: cfg.color, flexShrink: 0,
                }} />
                <span style={{ fontSize: 13, fontWeight: 700, color: NAVY }}>{group.heading}</span>
                <span style={{
                  background: cfg.bg, color: cfg.color,
                  borderRadius: 10, padding: '0 7px', fontSize: 10, fontWeight: 700,
                }}>
                  {groupSheets.length}
                </span>
              </div>

              {/* Sheets in group — collapsible */}
              {!isCollapsed && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {groupSheets.map(s => {
                    const isEditable = EDITABLE_STATUSES.has(s.status);
                    return (
                      <div
                        key={s.id}
                        onClick={() => navigate(isEditable ? `/horas/tarjeta/${s.id}` : `/horas/detalle/${s.id}`)}
                        style={{
                          ...bubbleStyle, padding: '14px 18px', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', gap: 14,
                          borderLeft: `3px solid ${cfg.color}`,
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: NAVY }}>{fmtDate(s.sheet_date)}</div>
                          <div style={{ fontSize: 12, color: '#94a3b8' }}>
                            Creado por: {s.supervisor_name || '—'} · {s.entry_count || 0} empleado{s.entry_count !== 1 ? 's' : ''} · {parseFloat(s.total_hours || 0).toFixed(1)}h
                          </div>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); setCloneSourceId(s.id); }}
                          title="Copiar tarjeta"
                          style={{
                            background: 'none', border: `1px solid ${BORDER_COLOR}`, borderRadius: 6,
                            cursor: 'pointer', padding: '4px 8px', display: 'flex', alignItems: 'center', gap: 4,
                            color: '#64748b', fontSize: 11, fontFamily: 'Inter, sans-serif',
                          }}
                        >
                          <Copy size={12} /> Copiar
                        </button>
                        <span style={{
                          padding: '3px 10px', borderRadius: 10,
                          background: cfg.bg, color: cfg.color,
                          fontSize: 11, fontWeight: 600,
                        }}>
                          {cfg.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })
      )}

      {/* Clone modal */}
      {cloneSourceId && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000,
        }}
          onClick={() => setCloneSourceId(null)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              ...bubbleStyle, padding: '20px 24px', width: 340,
              background: '#fff',
            }}
          >
            <h3 style={{ fontSize: 15, fontWeight: 700, color: NAVY, margin: '0 0 12px' }}>
              Copiar Tarjeta
            </h3>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
              Se copiarán los empleados y tareas. Las horas se inicializarán en 0 para que las modifique.
            </div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6 }}>
              Nueva fecha
            </label>
            <input
              type="date"
              value={cloneDate}
              onChange={e => setCloneDate(e.target.value)}
              style={{ ...fieldStyle, marginBottom: 16 }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setCloneSourceId(null)}
                style={{
                  background: '#f1f5f9', border: `1px solid ${BORDER_COLOR}`, borderRadius: 8,
                  padding: '7px 14px', fontSize: 12, cursor: 'pointer',
                  color: '#64748b', fontFamily: 'Inter, sans-serif',
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleClone}
                style={{ ...navyBtn, padding: '7px 14px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 5 }}
              >
                <Copy size={13} /> Copiar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
